/**
 * The preferences on this device.
 *
 * These never leave the browser: there is no account endpoint for them, so
 * they live in `localStorage` and only *prefill* controls. A stored value the
 * server no longer offers is ignored where it would be used, and shown here
 * as the custom value it is.
 */

import { useState } from 'react';
import type { Meta } from '../api/types.js';
import { loadSettings, saveSettings } from '../settings.js';

export interface SettingsScreenProps {
  meta: Meta | null;
}

export function SettingsScreen({ meta }: SettingsScreenProps) {
  const stored = loadSettings();
  const [recognitionMode, setRecognitionMode] = useState(stored.recognitionMode ?? '');
  const [containerKind, setContainerKind] = useState(stored.containerKind ?? '');
  const [saved, setSaved] = useState(false);

  const modes = meta?.vocabularies.recognitionModes ?? ['auto', 'fast', 'visual', 'manual'];
  const kinds = meta?.vocabularies.containerKinds ?? ['box', 'binder', 'drawer', 'case'];

  const save = (event: React.FormEvent): void => {
    event.preventDefault();
    saveSettings({
      recognitionMode: recognitionMode === '' ? null : recognitionMode,
      containerKind: containerKind === '' ? null : containerKind,
    });
    setSaved(true);
  };

  return (
    <div className="stack single">
      <header>
        <h2>Settings</h2>
        <p className="hint">
          Defaults that prefill forms on this device. They change what a form suggests, never what the server allows.
        </p>
      </header>

      <form className="card" onSubmit={save}>
        <label>
          Default recognition
          <select
            value={modes.includes(recognitionMode) ? recognitionMode : ''}
            onChange={(event) => {
              setRecognitionMode(event.target.value);
              setSaved(false);
            }}
          >
            <option value="">Server default</option>
            {modes.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">Prefills the recognition mode when a batch is opened on the Scan tab.</p>
        <label>
          Default container kind
          <select
            value={kinds.includes(containerKind) ? containerKind : ''}
            onChange={(event) => {
              setContainerKind(event.target.value);
              setSaved(false);
            }}
          >
            <option value="">Server default</option>
            {kinds.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">Prefills the kind when a container is added on the Bins tab.</p>
        <div className="form-foot">
          <button type="submit" className="primary">
            Save settings
          </button>
        </div>
        {saved && (
          <p className="hint" role="status">
            Saved on this device.
          </p>
        )}
      </form>
    </div>
  );
}
